import { createContext, useContext, useState, useCallback, useMemo } from "react";
import { workspaces, getWorkspaceData } from "./workspaceData";

// ── Scenarios (workspace-agnostic — applied contextually) ──
const scenarios = [
  { id: 'consultant-led', name: 'Consultant-led townhouse', desc: 'Consultant owns lodgement. Builder chases conditions, proof, and readiness.', notes: '12 Smith St is blocked at Pre-Slab. 3 conditions outstanding. Concreter on hold. David Patel overdue on soil report.' },
  { id: 'internal-approvals', name: 'Internal approvals workflow', desc: 'Builder PM owns everything internally. No external consultants leading.', notes: 'Sarah Chen manages all conditions. Lisa Nguyen handles admin. Mark Stevens is the surveyor.' },
  { id: 'readiness-trade', name: 'Readiness and trade control', desc: 'Focus on stage readiness gating trade release decisions.', notes: 'Pre-Slab blocked. Concreter locked. Frame trade not yet in pipeline. Release calendar shows upcoming windows.' },
  { id: 'client-supplied', name: 'Client-supplied finishes', desc: 'Client supplies tiles, fixtures, and appoints own tiler.', notes: 'Owner-supplied tiles due 28/01. Owner-appointed tiler booked for 15/02. Builder waiting on both before internal fit-out.' },
  { id: 'spec-home', name: 'Spec home buyer handover', desc: 'Spec build nearing completion. Buyer handover pack being prepared.', notes: '14 Bridge Rd is at Pre-OC. 82% complete. Buyer handover dossier is being assembled.' },
  { id: 'weak-proof', name: 'Stage blocked by weak proof', desc: 'Proof uploaded but flagged by AI as likely incomplete.', notes: 'Sediment control photo is too dark and blurry. AI recommends retake. Blocker stays until better photo uploaded.' },
  { id: 'missing-cert', name: 'Stage blocked by missing certificate', desc: 'Certificate required but not yet uploaded by consultant.', notes: 'Drainage certificate overdue from David Patel. Stage cannot clear without it.' },
  { id: 'consultant-overdue', name: 'Stage blocked by consultant overdue', desc: 'Consultant response is overdue. Chase sent but no reply.', notes: 'Emma Lawson traffic response overdue by 5 days. Escalation recommended.' },
  { id: 'stage-cleared', name: 'Stage just cleared, trade ready', desc: 'All Pre-Slab conditions just cleared. Concreter ready to release.', notes: 'All 4 Pre-Slab conditions approved. Trade moved to Ready. Surveyor notification can proceed.' },
  { id: 'surveyor-pack', name: 'Surveyor-ready pack almost complete', desc: 'Pack is 95% ready. One item pending final review.', notes: 'Drainage cert approved. Soil report approved. Stormwater approved. Setback survey pending final check.' },
];

// ── Deep clone helper ──
function dc(obj) { return JSON.parse(JSON.stringify(obj)); }

// ── Apply scenario transforms to workspace data ──
function applyScenario(data, scenarioId, workspaceId) {
  const s = scenarioId;
  let { conditions, blockers, trades, projects, evidence, gates, tradeAttendance, workProofs, tradeReadiness } = data;

  // Scenario transforms primarily affect Oakline's flagship project (proj-001)
  if (workspaceId === 'oakline') {
    if (s === 'stage-cleared') {
      conditions = conditions.map(c => c.projectId === 'proj-001' && c.stage === 'Pre-Slab' ? { ...c, status: 'cleared', isBlocker: false } : c);
      blockers = blockers.filter(b => b.projectId !== 'proj-001' || b.stage !== 'Pre-Slab');
      trades = trades.map(t => t.id === 'trade-001' ? { ...t, status: 'ready', blockersRemaining: 0, blockerIds: [] } : t);
      trades = trades.map(t => t.id === 'trade-003' ? { ...t, status: 'ready', blockersRemaining: 0, blockerIds: [] } : t);
      projects = projects.map(p => p.id === 'proj-001' ? { ...p, blockerCount: 0, status: 'on-track' } : p);
      gates = gates.map(g => g.id === 'gate-002' ? { ...g, state: 'cleared', clearedConditions: 4, blockers: [] } : g);
    } else if (s === 'weak-proof') {
      evidence = evidence.map(e => e.id === 'ev-002' ? { ...e, status: 'flagged', notes: 'AI flagged: Image too dark, blurry, full area not shown. Recommend retake in daylight.' } : e);
      blockers = [...blockers, { id: 'blk-008', projectId: 'proj-001', project: '12 Smith St Residence', conditionId: 'cond-002', conditionRef: '1.2', title: 'Sediment control photo flagged', type: 'weak-proof', stage: 'Pre-Start', owner: 'James Wheeler', ownerId: 'p2', ownerRole: 'Site Supervisor', status: 'action-needed', severity: 'warning', dueDate: '19/01/2025', trade: null, tradeImpact: null, readinessImpact: 'Pre-Start may need reverification', inspectionImpact: null, consequence: 'AI flagged poor quality. Re-upload needed.', createdDate: '17/01/2025', lastChased: null, chaseCount: 0, timeline: [{ date: '17/01/2025', action: 'AI flagged photo quality issue', type: 'created' }] }];
    } else if (s === 'surveyor-pack') {
      conditions = conditions.map(c => {
        if (c.projectId === 'proj-001' && c.stage === 'Pre-Slab') {
          if (c.id === 'cond-007') return { ...c, status: 'in-progress' };
          return { ...c, status: 'cleared', isBlocker: false };
        }
        return c;
      });
      blockers = blockers.filter(b => !(b.projectId === 'proj-001' && b.stage === 'Pre-Slab' && b.id !== 'blk-003'));
      blockers = blockers.map(b => b.id === 'blk-003' ? { ...b, title: 'Setback survey final check', status: 'in-progress', severity: 'warning' } : b);
      projects = projects.map(p => p.id === 'proj-001' ? { ...p, blockerCount: 1 } : p);
    }
  }

  return { ...data, conditions, blockers, trades, projects, evidence, gates, tradeAttendance, workProofs, tradeReadiness };
}

// ── Build full state for a workspace + scenario ──
function buildState(workspaceId, scenarioId) {
  const wsId = workspaceId || 'oakline';
  const scId = scenarioId || 'consultant-led';
  const ws = workspaces.find(w => w.id === wsId) || workspaces[0];
  const rawData = dc(getWorkspaceData(wsId));
  const data = applyScenario(rawData, scId, wsId);

  // Normalize trades with coordination defaults
  const normalizedTrades = data.trades.map(t => ({
    passStatus: 'none', siteReadyLink: null, crewCount: null, checkInTime: null,
    checkInDate: null, cannotStartReason: null, workProofs: [], signedOff: false,
    signedOffBy: null, signedOffDate: null,
    ...t,
  }));

  return {
    activeWorkspace: wsId,
    activeScenario: scId,
    activeProject: data.projects[0]?.id || null,
    workspace: { ...ws },
    projects: data.projects,
    people: data.people,
    conditions: data.conditions,
    evidence: data.evidence,
    blockers: data.blockers,
    trades: normalizedTrades,
    gates: data.gates,
    calendarEvents: data.calendarEvents,
    whatsappThreads: data.whatsappThreads,
    emailThreads: data.emailThreads,
    emailItems: data.emailItems,
    rfiItems: data.rfiItems,
    scopeItems: data.scopeItems,
    packs: data.packs,
    clients: data.clients,
    buyers: data.buyers,
    clientPortalData: data.clientPortalData,
    builderStats: data.builderStats,
    tradeAttendance: data.tradeAttendance || [],
    workProofs: data.workProofs || [],
    tradeReadiness: data.tradeReadiness || [],
    actionLog: [],
  };
}

// ── Compute derived builder stats from live state ──
function computeStats(state) {
  const active = state.projects.filter(p => p.status !== 'complete');
  return {
    atRiskJobs: active.filter(p => p.status === 'at-risk' || p.status === 'on-hold').length,
    blockedMilestones: state.gates.filter(g => g.state === 'locked').length,
    evidencePendingReview: state.evidence.filter(e => e.status === 'needs-review' || e.status === 'pending-match').length,
    newEmailsCaptured: state.emailItems.filter(e => e.status === 'pending-review').length,
    activeProjects: active.length,
    conditionsCleared: state.conditions.filter(c => c.status === 'cleared').length,
    conditionsTotal: state.conditions.length,
  };
}

// ── Context ──
const DemoStateContext = createContext(null);

export function useDemoState() {
  const ctx = useContext(DemoStateContext);
  if (!ctx) throw new Error('useDemoState must be used within DemoStateProvider');
  return ctx;
}

export { scenarios, workspaces, roleLenses };

// ── Role lenses ──
const roleLenses = [
  { id: 'builder-pm', label: 'Builder PM', emphasis: ['blockers', 'stage-risk', 'trade-release', 'delay-cost'], hidden: [], priority: 'Clear blockers, release trades, meet deadlines' },
  { id: 'office-admin', label: 'Office Admin', emphasis: ['follow-ups', 'proof-queue', 'due-dates', 'communication'], hidden: [], priority: 'Chase overdue items, process proof queue, send updates' },
  { id: 'site-supervisor', label: 'Site Supervisor', emphasis: ['uploads', 'stage-packs', 'qr-upload', 'proof-quality'], hidden: ['delay-cost'], priority: 'Upload site photos, check pack completeness' },
  { id: 'consultant', label: 'Consultant', emphasis: ['requested-items', 'due-dates', 'upload-links'], hidden: ['trade-release', 'delay-cost', 'internal-notes'], priority: 'Respond to requests, upload deliverables on time' },
  { id: 'surveyor', label: 'Surveyor', emphasis: ['readiness-pack', 'evidence-review', 'inspection-ready'], hidden: ['trade-release', 'delay-cost', 'internal-notes'], priority: 'Review readiness packs, schedule inspections' },
  { id: 'client', label: 'Client / Owner', emphasis: ['progress', 'milestones', 'owner-supplied'], hidden: ['blockers', 'trade-release', 'internal-notes', 'delay-cost'], priority: 'Track progress, confirm selections, view milestones' },
  { id: 'buyer', label: 'Buyer', emphasis: ['handover', 'manuals', 'defects', 'settlement'], hidden: ['blockers', 'trade-release', 'internal-notes', 'delay-cost', 'conditions'], priority: 'Review handover pack, report defects, prepare for settlement' },
];

// ── Analytics computations ──
function computeAnalytics(state) {
  const { blockers, conditions, evidence, trades, scopeItems, people } = state;
  // Blockers by cause
  const blockersByCause = {};
  blockers.forEach(b => { const t = b.type || 'other'; blockersByCause[t] = (blockersByCause[t] || 0) + 1; });
  // Overdue by owner
  const overdueByOwner = {};
  blockers.filter(b => b.status === 'overdue' || b.severity === 'critical').forEach(b => { overdueByOwner[b.owner] = (overdueByOwner[b.owner] || 0) + 1; });
  // Proof rejection/flag reasons
  const proofIssues = evidence.filter(e => e.status === 'flagged' || e.status === 'rejected').map(e => ({ file: e.fileName, reason: e.notes || 'Quality issue', condition: e.conditionTitle }));
  // Trade friction (trades on hold with most blockers)
  const tradeFriction = trades.filter(t => t.status === 'hold').map(t => ({ trade: t.trade, company: t.company, blockers: t.blockersRemaining, project: t.project })).sort((a, b) => b.blockers - a.blockers);
  // Owner-side dependency risk
  const ownerRisk = scopeItems.filter(s => (s.type === 'owner-supplied' || s.type === 'owner-appointed') && s.status !== 'complete' && s.status !== 'delivered').map(s => ({ item: s.item, type: s.type, dueDate: s.dueDate, impact: s.readinessImpact }));
  // Top blocker cause
  const topBlockerCause = Object.entries(blockersByCause).sort((a, b) => b[1] - a[1])[0];
  // Most overdue owner
  const topOverdueOwner = Object.entries(overdueByOwner).sort((a, b) => b[1] - a[1])[0];
  return { blockersByCause, overdueByOwner, proofIssues, tradeFriction, ownerRisk, topBlockerCause: topBlockerCause ? { cause: topBlockerCause[0], count: topBlockerCause[1] } : null, topOverdueOwner: topOverdueOwner ? { owner: topOverdueOwner[0], count: topOverdueOwner[1] } : null };
}

export function DemoStateProvider({ children }) {
  const [state, setState] = useState(() => buildState('oakline', 'consultant-led'));
  const [activeLens, setActiveLens] = useState('builder-pm');
  const [createdProjects, setCreatedProjects] = useState([]);

  // ── Workspace switching ──
  const switchWorkspace = useCallback((wsId) => {
    setState(prev => buildState(wsId, prev.activeScenario));
  }, []);

  // ── Scenario switching ──
  const switchScenario = useCallback((id) => {
    setState(prev => buildState(prev.activeWorkspace, id));
  }, []);

  const resetState = useCallback(() => {
    setState(prev => buildState(prev.activeWorkspace, prev.activeScenario));
  }, []);

  const nextScenario = useCallback(() => {
    setState(prev => {
      const idx = scenarios.findIndex(s => s.id === prev.activeScenario);
      const next = scenarios[(idx + 1) % scenarios.length];
      return buildState(prev.activeWorkspace, next.id);
    });
  }, []);

  const prevScenario = useCallback(() => {
    setState(prev => {
      const idx = scenarios.findIndex(s => s.id === prev.activeScenario);
      const p = scenarios[(idx - 1 + scenarios.length) % scenarios.length];
      return buildState(prev.activeWorkspace, p.id);
    });
  }, []);

  const setActiveProject = useCallback((projectId) => {
    setState(prev => ({ ...prev, activeProject: projectId }));
  }, []);

  // ── Core mutation: Approve evidence (cascades to conditions, blockers, trades, gates, projects) ──
  const approveEvidence = useCallback((evidenceId) => {
    setState(prev => {
      const ev = prev.evidence.find(e => e.id === evidenceId);
      if (!ev) return prev;
      const newEvidence = prev.evidence.map(e => e.id === evidenceId ? { ...e, status: 'approved' } : e);
      let newConditions = prev.conditions;
      if (ev.conditionId) {
        newConditions = prev.conditions.map(c => c.id === ev.conditionId ? { ...c, status: 'cleared', isBlocker: false } : c);
      }
      const clearedCondId = ev.conditionId;
      let newBlockers = prev.blockers;
      if (clearedCondId) {
        newBlockers = prev.blockers.filter(b => b.conditionId !== clearedCondId);
      }
      const newTrades = prev.trades.map(t => {
        const actualBids = t.blockerIds.filter(bid => newBlockers.some(nb => nb.id === bid));
        return { ...t, blockerIds: actualBids, blockersRemaining: actualBids.length, status: actualBids.length === 0 && t.status === 'hold' ? 'ready' : t.status };
      });
      // Update gates
      const newGates = prev.gates.map(g => {
        if (g.projectId !== ev.projectId) return g;
        const projConditions = newConditions.filter(c => c.projectId === g.projectId && c.stage === g.stage);
        const cleared = projConditions.filter(c => c.status === 'cleared').length;
        const remaining = projConditions.filter(c => c.isBlocker);
        return {
          ...g,
          clearedConditions: cleared,
          blockers: remaining.map(c => ({ conditionId: c.id, title: c.title, status: c.status, owner: c.owner })),
          state: remaining.length === 0 && cleared === g.totalConditions ? 'cleared' : g.state,
        };
      });
      // Update project blocker counts
      const newProjects = prev.projects.map(p => {
        const pBlockers = newBlockers.filter(b => b.projectId === p.id).length;
        return { ...p, blockerCount: pBlockers, status: pBlockers === 0 && p.status === 'at-risk' ? 'on-track' : p.status };
      });
      const newLog = [{ id: `log-${Date.now()}`, action: `Evidence approved: ${ev.fileName}`, time: new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }), type: 'approve' }, ...prev.actionLog];
      return { ...prev, evidence: newEvidence, conditions: newConditions, blockers: newBlockers, trades: newTrades, gates: newGates, projects: newProjects, actionLog: newLog };
    });
  }, []);

  // ── Core mutation: Resolve blocker (cascades to trades, projects, gates) ──
  const resolveBlocker = useCallback((blockerId) => {
    setState(prev => {
      const blk = prev.blockers.find(b => b.id === blockerId);
      if (!blk) return prev;
      const newBlockers = prev.blockers.filter(b => b.id !== blockerId);
      const newTrades = prev.trades.map(t => {
        const actualBids = t.blockerIds.filter(bid => newBlockers.some(nb => nb.id === bid));
        return { ...t, blockerIds: actualBids, blockersRemaining: actualBids.length, status: actualBids.length === 0 && t.status === 'hold' ? 'ready' : t.status };
      });
      // Also clear the associated condition if any
      let newConditions = prev.conditions;
      if (blk.conditionId) {
        newConditions = prev.conditions.map(c => c.id === blk.conditionId ? { ...c, status: 'cleared', isBlocker: false } : c);
      }
      // Update gates
      const newGates = prev.gates.map(g => {
        if (g.projectId !== blk.projectId) return g;
        const projBlockers = newBlockers.filter(b => b.projectId === g.projectId && b.stage === g.stage);
        const projConditions = newConditions.filter(c => c.projectId === g.projectId && c.stage === g.stage);
        const cleared = projConditions.filter(c => c.status === 'cleared').length;
        return {
          ...g,
          clearedConditions: cleared,
          blockers: projBlockers.map(b => ({ conditionId: b.conditionId, title: b.title, status: b.status, owner: b.owner })),
          state: projBlockers.length === 0 && cleared === g.totalConditions ? 'cleared' : g.state,
        };
      });
      const newProjects = prev.projects.map(p => {
        const pBlockers = newBlockers.filter(b => b.projectId === p.id).length;
        return { ...p, blockerCount: pBlockers, status: pBlockers === 0 && p.status === 'at-risk' ? 'on-track' : p.status };
      });
      const newLog = [{ id: `log-${Date.now()}`, action: `Blocker resolved: ${blk.title}`, time: new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }), type: 'resolve' }, ...prev.actionLog];
      return { ...prev, conditions: newConditions, blockers: newBlockers, trades: newTrades, gates: newGates, projects: newProjects, actionLog: newLog };
    });
  }, []);

  // ── Core mutation: Release trade ──
  const releaseTrade = useCallback((tradeId) => {
    setState(prev => {
      const trd = prev.trades.find(t => t.id === tradeId);
      const newTrades = prev.trades.map(t => t.id === tradeId ? { ...t, status: 'released' } : t);
      const newLog = [{ id: `log-${Date.now()}`, action: `Trade released: ${trd?.trade} - ${trd?.company}`, time: new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }), type: 'release' }, ...prev.actionLog];
      return { ...prev, trades: newTrades, actionLog: newLog };
    });
  }, []);

  // ── Mutation: Upload evidence (simulated) ──
  const uploadEvidence = useCallback((projectId, conditionId, fileName) => {
    setState(prev => {
      const proj = prev.projects.find(p => p.id === projectId);
      const cond = prev.conditions.find(c => c.id === conditionId);
      const newEv = {
        id: `ev-${Date.now()}`, conditionId, conditionTitle: cond?.title || null, conditionNumber: cond?.number || null,
        project: proj?.name || '', projectId, fileName: fileName || 'Upload.pdf', fileType: 'pdf',
        uploadedBy: 'You', uploadedById: 'user', uploadMethod: 'Drag and drop',
        uploadDate: new Date().toLocaleDateString('en-AU'), status: 'needs-review',
        matchConfidence: 95, fileSize: '1.2 MB', reviewer: null, notes: '',
      };
      const newLog = [{ id: `log-${Date.now()}`, action: `Evidence uploaded: ${fileName || 'file'}`, time: new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }), type: 'upload' }, ...prev.actionLog];
      return { ...prev, evidence: [...prev.evidence, newEv], actionLog: newLog };
    });
  }, []);

  // ── Mutation: Send chase ──
  const sendChase = useCallback((blockerId, method) => {
    setState(prev => {
      const blk = prev.blockers.find(b => b.id === blockerId);
      if (!blk) return prev;
      const newBlockers = prev.blockers.map(b => b.id === blockerId ? {
        ...b, lastChased: new Date().toLocaleDateString('en-AU'), chaseCount: b.chaseCount + 1,
        timeline: [...b.timeline, { date: new Date().toLocaleDateString('en-AU'), action: `${method === 'whatsapp' ? 'WhatsApp' : 'Email'} chase sent to ${b.owner}`, type: 'chase' }],
      } : b);
      const newLog = [{ id: `log-${Date.now()}`, action: `Chase sent to ${blk.owner} via ${method || 'email'}`, time: new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }), type: 'chase' }, ...prev.actionLog];
      return { ...prev, blockers: newBlockers, actionLog: newLog };
    });
  }, []);

  // ── Mutation: Add calendar event ──
  const addCalendarEvent = useCallback((event) => {
    setState(prev => ({
      ...prev,
      calendarEvents: [...prev.calendarEvents, { id: `cal-${Date.now()}`, ...event }],
      actionLog: [{ id: `log-${Date.now()}`, action: `Calendar event added: ${event.title}`, time: new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }), type: 'calendar' }, ...prev.actionLog],
    }));
  }, []);

  // ── Trade Coordination Mutations ──

  const generateTradePass = useCallback((tradeId) => {
    setState(prev => {
      const trd = prev.trades.find(t => t.id === tradeId);
      if (!trd) return prev;
      const link = `https://app.easypermit.com.au/pass/${tradeId}`;
      const newTrades = prev.trades.map(t => t.id === tradeId ? { ...t, passStatus: 'generated', siteReadyLink: link } : t);
      return { ...prev, trades: newTrades, actionLog: [{ id: `log-${Date.now()}`, action: `SiteReady Trade Pass generated for ${trd.trade} (${trd.company})`, time: new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }), type: 'trade-pass' }, ...prev.actionLog] };
    });
  }, []);

  const sendTradePassLink = useCallback((tradeId) => {
    setState(prev => {
      const trd = prev.trades.find(t => t.id === tradeId);
      if (!trd) return prev;
      const newTrades = prev.trades.map(t => t.id === tradeId ? { ...t, status: 'link-sent', passStatus: 'link-sent' } : t);
      const proj = prev.projects.find(p => p.id === trd.projectId);
      const newProjects = prev.projects.map(p => p.id === trd.projectId ? { ...p, activityTimeline: [{ date: new Date().toLocaleDateString('en-AU'), action: `SiteReady link sent to ${trd.contact} (${trd.trade})`, actor: 'You', type: 'link-sent' }, ...(p.activityTimeline || [])] } : p);
      return { ...prev, trades: newTrades, projects: newProjects, actionLog: [{ id: `log-${Date.now()}`, action: `SiteReady link sent to ${trd.contact} via ${trd.channel}`, time: new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }), type: 'link-sent' }, ...prev.actionLog] };
    });
  }, []);

  const checkInTrade = useCallback((tradeId, crewCount) => {
    setState(prev => {
      const trd = prev.trades.find(t => t.id === tradeId);
      if (!trd) return prev;
      const now = new Date();
      const timeStr = now.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
      const dateStr = now.toLocaleDateString('en-AU');
      const newTrades = prev.trades.map(t => t.id === tradeId ? { ...t, status: 'checked-in', passStatus: 'checked-in', crewCount, checkInTime: timeStr, checkInDate: dateStr } : t);
      const newAttendance = [...prev.tradeAttendance, { id: `att-${Date.now()}`, tradeId, projectId: trd.projectId, trade: trd.trade, company: trd.company, contact: trd.contact, checkInTime: timeStr, checkInDate: dateStr, crewCount, status: 'on-site', notes: `${crewCount} crew checked in`, expectedDuration: '8 hours' }];
      const newProjects = prev.projects.map(p => p.id === trd.projectId ? { ...p, activityTimeline: [{ date: dateStr, action: `${trd.trade} checked in with ${crewCount} crew`, actor: trd.contact, type: 'check-in' }, ...(p.activityTimeline || [])] } : p);
      return { ...prev, trades: newTrades, tradeAttendance: newAttendance, projects: newProjects, actionLog: [{ id: `log-${Date.now()}`, action: `${trd.trade} (${trd.company}) checked in — ${crewCount} crew on site`, time: timeStr, type: 'check-in' }, ...prev.actionLog] };
    });
  }, []);

  const markCannotStart = useCallback((tradeId, reason) => {
    setState(prev => {
      const trd = prev.trades.find(t => t.id === tradeId);
      if (!trd) return prev;
      const now = new Date();
      const timeStr = now.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
      const dateStr = now.toLocaleDateString('en-AU');
      const newTrades = prev.trades.map(t => t.id === tradeId ? { ...t, status: 'cannot-start', passStatus: 'cannot-start', cannotStartReason: reason } : t);
      const newBlocker = { id: `blk-cs-${Date.now()}`, projectId: trd.projectId, project: trd.project, conditionId: null, conditionRef: null, title: `Cannot Start: ${trd.trade} — ${reason}`, type: 'cannot-start', stage: trd.stage, owner: trd.contact, ownerId: null, ownerRole: trd.trade, status: 'action-needed', severity: 'critical', dueDate: dateStr, trade: trd.trade, tradeImpact: `${trd.trade} on site but unable to commence`, readinessImpact: `${trd.stage} delayed`, inspectionImpact: null, consequence: reason, createdDate: dateStr, lastChased: null, chaseCount: 0, timeline: [{ date: dateStr, action: `Cannot Start reported by ${trd.contact}: ${reason}`, type: 'created' }] };
      const newAttendance = prev.tradeAttendance.map(a => a.tradeId === tradeId ? { ...a, status: 'cannot-start', notes: reason } : a);
      const newProjects = prev.projects.map(p => p.id === trd.projectId ? { ...p, blockerCount: p.blockerCount + 1, activityTimeline: [{ date: dateStr, action: `Cannot Start: ${trd.trade} — ${reason}`, actor: trd.contact, type: 'cannot-start' }, ...(p.activityTimeline || [])] } : p);
      return { ...prev, trades: newTrades, blockers: [...prev.blockers, newBlocker], tradeAttendance: newAttendance, projects: newProjects, actionLog: [{ id: `log-${Date.now()}`, action: `Cannot Start: ${trd.trade} — ${reason}`, time: timeStr, type: 'cannot-start' }, ...prev.actionLog] };
    });
  }, []);

  const resolveCannotStart = useCallback((tradeId) => {
    setState(prev => {
      const trd = prev.trades.find(t => t.id === tradeId);
      if (!trd) return prev;
      const timeStr = new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
      const newTrades = prev.trades.map(t => t.id === tradeId ? { ...t, status: 'checked-in', passStatus: 'checked-in', cannotStartReason: null } : t);
      const newBlockers = prev.blockers.filter(b => !(b.type === 'cannot-start' && b.trade === trd.trade && b.projectId === trd.projectId));
      const removedCount = prev.blockers.length - newBlockers.length;
      const newAttendance = prev.tradeAttendance.map(a => a.tradeId === tradeId ? { ...a, status: 'on-site', notes: 'Cannot Start resolved' } : a);
      const newProjects = prev.projects.map(p => p.id === trd.projectId ? { ...p, blockerCount: Math.max(0, p.blockerCount - removedCount), activityTimeline: [{ date: new Date().toLocaleDateString('en-AU'), action: `Cannot Start resolved for ${trd.trade}`, actor: 'Supervisor', type: 'resolve' }, ...(p.activityTimeline || [])] } : p);
      return { ...prev, trades: newTrades, blockers: newBlockers, tradeAttendance: newAttendance, projects: newProjects, actionLog: [{ id: `log-${Date.now()}`, action: `Cannot Start resolved: ${trd.trade} can now proceed`, time: timeStr, type: 'resolve' }, ...prev.actionLog] };
    });
  }, []);

  const submitWorkProof = useCallback((tradeId, fileName, notes) => {
    setState(prev => {
      const trd = prev.trades.find(t => t.id === tradeId);
      if (!trd) return prev;
      const timeStr = new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
      const dateStr = new Date().toLocaleDateString('en-AU');
      const wpId = `wp-${Date.now()}`;
      const newWp = { id: wpId, tradeId, projectId: trd.projectId, trade: trd.trade, company: trd.company, type: 'work-proof', fileName: fileName || 'WorkProof.jpg', fileType: 'image', uploadDate: dateStr, uploadTime: timeStr, uploadedBy: trd.contact, status: 'pending', notes: notes || '', reviewedBy: null, reviewedDate: null };
      const newTrades = prev.trades.map(t => t.id === tradeId ? { ...t, passStatus: 'work-proof-submitted', workProofs: [...(t.workProofs || []), wpId] } : t);
      const newProjects = prev.projects.map(p => p.id === trd.projectId ? { ...p, activityTimeline: [{ date: dateStr, action: `Work proof uploaded by ${trd.contact} (${trd.trade})`, actor: trd.contact, type: 'work-proof' }, ...(p.activityTimeline || [])] } : p);
      return { ...prev, trades: newTrades, workProofs: [...prev.workProofs, newWp], projects: newProjects, actionLog: [{ id: `log-${Date.now()}`, action: `Work proof uploaded: ${fileName || 'WorkProof.jpg'} by ${trd.contact}`, time: timeStr, type: 'work-proof' }, ...prev.actionLog] };
    });
  }, []);

  const approveWorkProof = useCallback((wpId) => {
    setState(prev => {
      const wp = prev.workProofs.find(w => w.id === wpId);
      if (!wp) return prev;
      const timeStr = new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
      const newWps = prev.workProofs.map(w => w.id === wpId ? { ...w, status: 'approved', reviewedBy: 'James Wheeler', reviewedDate: new Date().toLocaleDateString('en-AU') } : w);
      return { ...prev, workProofs: newWps, actionLog: [{ id: `log-${Date.now()}`, action: `Work proof approved: ${wp.fileName}`, time: timeStr, type: 'approve' }, ...prev.actionLog] };
    });
  }, []);

  const signOffTrade = useCallback((tradeId) => {
    setState(prev => {
      const trd = prev.trades.find(t => t.id === tradeId);
      if (!trd) return prev;
      const timeStr = new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
      const dateStr = new Date().toLocaleDateString('en-AU');
      const newTrades = prev.trades.map(t => t.id === tradeId ? { ...t, status: 'signed-off', passStatus: 'signed-off', signedOff: true, signedOffBy: 'James Wheeler', signedOffDate: dateStr } : t);
      const newAttendance = prev.tradeAttendance.map(a => a.tradeId === tradeId ? { ...a, status: 'checked-out' } : a);
      const newProjects = prev.projects.map(p => p.id === trd.projectId ? { ...p, activityTimeline: [{ date: dateStr, action: `${trd.trade} signed off by supervisor`, actor: 'James Wheeler', type: 'sign-off' }, ...(p.activityTimeline || [])] } : p);
      return { ...prev, trades: newTrades, tradeAttendance: newAttendance, projects: newProjects, actionLog: [{ id: `log-${Date.now()}`, action: `Trade signed off: ${trd.trade} (${trd.company})`, time: timeStr, type: 'sign-off' }, ...prev.actionLog] };
    });
  }, []);

  // ── Generic log action ──
  const logAction = useCallback((action, type = 'action') => {
    setState(prev => ({
      ...prev,
      actionLog: [{ id: `log-${Date.now()}`, action, time: new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }), type }, ...prev.actionLog],
    }));
  }, []);

  // ── Project memory helper: get all data for a specific project ──
  const getProjectMemory = useCallback((projectId) => {
    const pid = projectId || state.activeProject;
    const project = state.projects.find(p => p.id === pid);
    if (!project) return null;
    return {
      project,
      conditions: state.conditions.filter(c => c.projectId === pid),
      evidence: state.evidence.filter(e => e.projectId === pid),
      blockers: state.blockers.filter(b => b.projectId === pid),
      trades: state.trades.filter(t => t.projectId === pid),
      gates: state.gates.filter(g => g.projectId === pid),
      calendarEvents: state.calendarEvents.filter(e => e.projectId === pid),
      whatsappThreads: state.whatsappThreads.filter(w => w.projectId === pid),
      emailThreads: state.emailThreads.filter(e => e.projectId === pid),
      emailItems: state.emailItems.filter(e => e.projectId === pid),
      rfiItems: state.rfiItems.filter(r => r.projectId === pid),
      scopeItems: state.scopeItems.filter(s => s.projectId === pid),
      packs: state.packs.filter(p => p.projectId === pid),
      client: state.clients.find(c => c.projectId === pid) || null,
      buyer: state.buyers.find(b => b.projectId === pid) || null,
      tradeAttendance: state.tradeAttendance.filter(a => a.projectId === pid),
      workProofs: state.workProofs.filter(w => w.projectId === pid),
      tradeReadiness: state.tradeReadiness.filter(r => r.projectId === pid),
    };
  }, [state]);

  // ── Compute live builder stats ──
  const liveStats = useMemo(() => computeStats(state), [state]);
  const analytics = useMemo(() => computeAnalytics(state), [state]);
  const lens = useMemo(() => roleLenses.find(l => l.id === activeLens) || roleLenses[0], [activeLens]);

  // ── Create project (simulation) ──
  const createProject = useCallback((projectData) => {
    const newId = `proj-new-${Date.now()}`;
    const newProject = {
      id: newId, name: projectData.name, address: `${projectData.address}, ${projectData.suburb} ${projectData.state} ${projectData.postcode}`,
      council: projectData.council || 'Detected', permitNumber: `EP-${Date.now().toString(36).toUpperCase()}`, stage: projectData.stage || 'Pre-Start', status: 'on-track',
      blockerCount: 0, conditionCount: 0, rfiPending: 0, evidencePending: 0, completionPercent: 0,
      owner: projectData.pm || state.people[0]?.name || 'Builder PM', lastActivity: 'Just now',
      clientId: null, buyerId: null, visibility: { clientPortal: false, buyerHandover: false, surveyorPack: false },
      activityTimeline: [{ date: new Date().toLocaleDateString('en-AU'), action: 'Project created via easypermit', actor: 'System', type: 'created' }],
      versionHistory: [{ version: 'v1.0', date: new Date().toLocaleDateString('en-AU'), changes: 'Project created' }],
      projectType: projectData.projectType, operatingModel: projectData.operatingModel,
    };
    setCreatedProjects(prev => [...prev, newProject]);
    setState(prev => ({
      ...prev,
      projects: [...prev.projects, newProject],
      actionLog: [{ id: `log-${Date.now()}`, action: `Project created: ${projectData.name}`, time: new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }), type: 'create' }, ...prev.actionLog],
    }));
    return newId;
  }, [state.people]);

  const value = useMemo(() => ({
    ...state,
    builderStats: liveStats,
    analytics,
    activeLens, setActiveLens, lens, roleLenses,
    createdProjects,
    // Switchers
    switchWorkspace, switchScenario, resetState, nextScenario, prevScenario,
    // Selection
    setActiveProject,
    // Core mutations
    approveEvidence, resolveBlocker, releaseTrade, createProject,
    // Existing mutations
    uploadEvidence, sendChase, addCalendarEvent,
    // Trade coordination mutations
    generateTradePass, sendTradePassLink, checkInTrade, markCannotStart,
    resolveCannotStart, submitWorkProof, approveWorkProof, signOffTrade,
    // Helpers
    logAction, getProjectMemory,
    // Constants
    scenarios, workspaces,
  }), [state, liveStats, analytics, activeLens, lens, createdProjects, switchWorkspace, switchScenario, resetState, nextScenario, prevScenario, setActiveProject, approveEvidence, resolveBlocker, releaseTrade, createProject, uploadEvidence, sendChase, addCalendarEvent, generateTradePass, sendTradePassLink, checkInTrade, markCannotStart, resolveCannotStart, submitWorkProof, approveWorkProof, signOffTrade, logAction, getProjectMemory]);

  return (
    <DemoStateContext.Provider value={value}>
      {children}
    </DemoStateContext.Provider>
  );
}
